/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import React, {useEffect, useMemo, useState} from "react";
import {useHasWriteAccess} from "../../../../../redux/ducks/AccessControl/accessControlHelpers";
import {
  EAdasProgramsLoadingStateTypes,
  orderTableDataByReportOrder,
  selectAdasProgramsArchive,
  selectAdasProgramsMetaData,
  selectIsCoverageRequirementsReadOnlyState,
  selectSensingSystemRequirementsLoadingState,
  selectSensingSystemRequirementsLoadingStateTypeIsLoading,
  selectSensingSystemRequirementsTableDataSetStatusMap,
  selectSensingSystemRequirementsTableIsSaved,
  selectSensingSystemRequirementsTableMergeConfigurations,
  selectSensingSystemRequirementsTableRowOrder,
  selectSensingSystemRequirementsTableRows,
  selectSensingSystemRequirementsTableSelectedRows,
  selectSensorComponentRequirementsLoadingState,
  selectSensorComponentRequirementsLoadingStateTypeIsLoading,
  selectSensorComponentRequirementsTableDataSetStatusMap,
  selectSensorComponentRequirementsTableIsSaved,
  selectSensorComponentRequirementsTableMergeConfigurations,
  selectSensorComponentRequirementsTableRowOrder,
  selectSensorComponentRequirementsTableRows,
  selectSensorComponentRequirementsTableSelectedRows,
  selectWorldModelSolutionRequirementsLoadingState,
  selectWorldModelSolutionRequirementsLoadingStateTypeIsLoading,
  selectWorldModelSolutionRequirementsTableDataSetStatusMap,
  selectWorldModelSolutionRequirementsTableIsSaved,
  selectWorldModelSolutionRequirementsTableMergeConfigurations,
  selectWorldModelSolutionRequirementsTableRowOrder,
  selectWorldModelSolutionRequirementsTableRows,
  selectWorldModelSolutionRequirementsTableSelectedRows,
  setSensingSystemRequirementsLoadingState,
  setSensingSystemRequirementsTableDataSetStatusMap,
  setSensingSystemRequirementsTableIsSaved,
  setSensingSystemRequirementsTableMergeConfigurations,
  setSensingSystemRequirementsTableRowOrder,
  setSensingSystemRequirementsTableRows,
  setSensingSystemRequirementsTableSelectedRows,
  setSensorComponentRequirementsLoadingState,
  setSensorComponentRequirementsTableDataSetStatusMap,
  setSensorComponentRequirementsTableIsSaved,
  setSensorComponentRequirementsTableMergeConfigurations,
  setSensorComponentRequirementsTableRowOrder,
  setSensorComponentRequirementsTableRows,
  setSensorComponentRequirementsTableSelectedRows,
  setWorldModelSolutionRequirementsLoadingState,
  setWorldModelSolutionRequirementsTableDataSetStatusMap,
  setWorldModelSolutionRequirementsTableIsSaved,
  setWorldModelSolutionRequirementsTableMergeConfigurations,
  setWorldModelSolutionRequirementsTableRowOrder,
  setWorldModelSolutionRequirementsTableRows,
  setWorldModelSolutionRequirementsTableSelectedRows
} from "../../../../../redux/ducks/AdasPrograms/slice";
import {useAppDispatch, useAppSelector} from "../../../../../redux/hooks";
import ArgosFlexbox from "../../../../components/Containers/ArgosFlexbox";
import {AdasProgramsReportPageIdx, AdasProgramsReportPages, BaseReportsTableData, GreenLineReportsTableData, RedLineReportsTableData, ReportsTableData} from "./constants";
import {RowActions} from "./TableParts/RowActions";
import {ReportsTableRowActionTypes} from "./TableParts/types";
import {LeftToolbarActions} from "./TableParts/LeftToolbarActions";
import {RightToolbarActions} from "./TableParts/RightToolbarActions";
import {MRT_Cell, MRT_ColumnDef, MRT_RowSelectionState} from "material-react-table";
import {ARGOS_REPORTS_STATE_TO_COLOR, EArgosReportsState} from "../../../../../constants/colors";
import {getUrlForDataset, renderLink} from "../../../CoverageRequirements/ItemDetails/Tabs/CoverageRequirementsTab/RightPanelComponents/DrivingScenarioProperties";
import {ManageDataSourcesDialog} from "./Dialogs/ManageDataSourcesDialog";
import {NewReportDialog} from "./Dialogs/NewReportDialog";
import {usePostRequest} from "../../../../../api/helper";
import {CAdasProgramsRequests} from "../types";
import toast from "react-hot-toast";
import {cloneDeep} from "lodash";
import ADAS_PROGRAMS_CONSTANTS from "../../constants";
import {ViewReportDialog, viewReportDialogSlice} from "./Dialogs/ViewReportDialog";
import {StringParam, useQueryParam} from "use-query-params";
import {DeleteReportDialog, deleteReportDialogSlice} from "./Dialogs/DeleteReportDialog";
import {EditReportDialog, editReportDialogSlice} from "./Dialogs/EditReportDialog";
import {UpdateReportDialog} from "./Dialogs/UpdateReportDialog";
import {getPageFromHtabIndex} from "./helpers";
import {AppDispatch} from "../../../../../redux/store";
import VAR_COMMON_CONSTANTS from "../../../../../constants/globalVariables";
import { ViewFullRequirementTextDialog, viewFullRequirementTextDialogSlice } from "./Dialogs/ViewFullRequirementTextDialog";
import {ArgosTableSimple} from "../../../../components/Table/ArgosTableSimple";


export const tableSlice = {
  [AdasProgramsReportPages.SENSING_SYSTEM_REQUIREMENT]: {
    setRows: setSensingSystemRequirementsTableRows,
    selectRows: selectSensingSystemRequirementsTableRows,
    setMergeConfigurations: setSensingSystemRequirementsTableMergeConfigurations,
    selectMergeConfigurations: selectSensingSystemRequirementsTableMergeConfigurations,
    setRowOrder: setSensingSystemRequirementsTableRowOrder,
    selectRowOrder: selectSensingSystemRequirementsTableRowOrder,
    setIsSaved: setSensingSystemRequirementsTableIsSaved,
    selectIsSaved: selectSensingSystemRequirementsTableIsSaved,
    setDataSetStatusMap: setSensingSystemRequirementsTableDataSetStatusMap,
    selectDataSetStatusMap: selectSensingSystemRequirementsTableDataSetStatusMap,
    setSelectedRows: setSensingSystemRequirementsTableSelectedRows,
    selectSelectedRows: selectSensingSystemRequirementsTableSelectedRows,
    setRowOrderEndpoint: CAdasProgramsRequests.coverageRequirementsReportOrder.url,
    getReportsTableUrl: CAdasProgramsRequests.getReportsTable.url,
    setLoadingState: setSensingSystemRequirementsLoadingState,
    selectLoadingState: selectSensingSystemRequirementsLoadingState,
    selectTypeLoadingState: selectSensingSystemRequirementsLoadingStateTypeIsLoading,
    saveTableEndpoint: CAdasProgramsRequests.saveReportsTable.url,
  },
  [AdasProgramsReportPages.SENSOR_COMPONENT_REQUIREMENT]: {
    setRows: setSensorComponentRequirementsTableRows,
    selectRows: selectSensorComponentRequirementsTableRows,
    setMergeConfigurations: setSensorComponentRequirementsTableMergeConfigurations,
    selectMergeConfigurations: selectSensorComponentRequirementsTableMergeConfigurations,
    setRowOrder: setSensorComponentRequirementsTableRowOrder,
    selectRowOrder: selectSensorComponentRequirementsTableRowOrder,
    setIsSaved: setSensorComponentRequirementsTableIsSaved,
    selectIsSaved: selectSensorComponentRequirementsTableIsSaved,
    setDataSetStatusMap: setSensorComponentRequirementsTableDataSetStatusMap,
    selectDataSetStatusMap: selectSensorComponentRequirementsTableDataSetStatusMap,
    setSelectedRows: setSensorComponentRequirementsTableSelectedRows,
    selectSelectedRows: selectSensorComponentRequirementsTableSelectedRows,
    setRowOrderEndpoint: ADAS_PROGRAMS_CONSTANTS.itemDetails.sensorComponentRequirements.endpoints.reorderTableRows,
    getReportsTableUrl: ADAS_PROGRAMS_CONSTANTS.itemDetails.sensorComponentRequirements.endpoints.getReportsTable,
    setLoadingState: setSensorComponentRequirementsLoadingState,
    selectLoadingState: selectSensorComponentRequirementsLoadingState,
    selectTypeLoadingState: selectSensorComponentRequirementsLoadingStateTypeIsLoading,
    saveTableEndpoint: ADAS_PROGRAMS_CONSTANTS.itemDetails.sensorComponentRequirements.endpoints.saveReportsTable,
  },
  [AdasProgramsReportPages.WORLD_MODEL_SOLUTION_REQUIREMENT]: {
    setRows: setWorldModelSolutionRequirementsTableRows,
    selectRows: selectWorldModelSolutionRequirementsTableRows,
    setMergeConfigurations: setWorldModelSolutionRequirementsTableMergeConfigurations,
    selectMergeConfigurations: selectWorldModelSolutionRequirementsTableMergeConfigurations,
    setRowOrder: setWorldModelSolutionRequirementsTableRowOrder,
    selectRowOrder: selectWorldModelSolutionRequirementsTableRowOrder,
    setIsSaved: setWorldModelSolutionRequirementsTableIsSaved,
    selectIsSaved: selectWorldModelSolutionRequirementsTableIsSaved,
    setDataSetStatusMap: setWorldModelSolutionRequirementsTableDataSetStatusMap,
    selectDataSetStatusMap: selectWorldModelSolutionRequirementsTableDataSetStatusMap,
    setSelectedRows: setWorldModelSolutionRequirementsTableSelectedRows,
    selectSelectedRows: selectWorldModelSolutionRequirementsTableSelectedRows,
    setRowOrderEndpoint: ADAS_PROGRAMS_CONSTANTS.itemDetails.worldModelSolutionRequirements.endpoints.reorderTableRows,
    getReportsTableUrl: ADAS_PROGRAMS_CONSTANTS.itemDetails.worldModelSolutionRequirements.endpoints.getReportsTable,
    setLoadingState: setWorldModelSolutionRequirementsLoadingState,
    selectLoadingState: selectWorldModelSolutionRequirementsLoadingState,
    selectTypeLoadingState: selectWorldModelSolutionRequirementsLoadingStateTypeIsLoading,
    saveTableEndpoint: ADAS_PROGRAMS_CONSTANTS.itemDetails.worldModelSolutionRequirements.endpoints.saveReportsTable,
  },
}

type ReportsTableResponse = {
  tableData: ReportsTableData[]
  mergeConfigurations: string[]
  reportOrder: string[]
}

export const filterAndSetTableData = (res: ReportsTableResponse, dispatch: AppDispatch, page: AdasProgramsReportPages, fromBackend: boolean = true) => {
  const _tableData = res.tableData
  const _reportOrder = res.reportOrder

  if (_tableData != null) {
    dispatch(tableSlice[page].setRows(_tableData));
    dispatch(tableSlice[page].setRowOrder(_reportOrder))
    if (fromBackend) { // TODO: How does this work if an edit is started and then this executes? Will the edit be lost and the save button disabled? Or does the edit stay there but the save button is still disabled?
      dispatch(tableSlice[page].setIsSaved(true))
    }

    // for each report, create a map of reportId to datasetState
    const dataSetStatusMap: Record<string, EArgosReportsState> = {}
    _tableData.forEach((row) => {
      const reportId = row.reportId
      const datasetState = row.dataSetState
      if (reportId == null || datasetState == null) {
        return
      }
      dataSetStatusMap[reportId] = datasetState
    })
    dispatch(tableSlice[page].setDataSetStatusMap({dataSetStatusMap}))
  }
  const mergeConfigurations = res.mergeConfigurations
  if (mergeConfigurations != null) {
    dispatch(tableSlice[page].setMergeConfigurations(mergeConfigurations))
  }
}


export const ReportsTable = () => {
  const dispatch = useAppDispatch()

  const htabid = ADAS_PROGRAMS_CONSTANTS.itemDetails.horizontalTabs.pageHtabs.tabIndexUrlKey
  const [htab, setHtab] = useQueryParam(htabid, StringParam)
  const page = useMemo(() => getPageFromHtabIndex(htab as AdasProgramsReportPageIdx), [htab])
  const reduxIsProgramArchive = useAppSelector(state => selectAdasProgramsArchive(state))
  const hasWriteAccess = useHasWriteAccess()
  const isReadOnlyProgram = useAppSelector(state => selectIsCoverageRequirementsReadOnlyState(state)) || reduxIsProgramArchive
  /** TODO: commonize readonly state */
  const dataSetStatusMap = useAppSelector(tableSlice[page].selectDataSetStatusMap)

  const adasProgramMetaData = useAppSelector(selectAdasProgramsMetaData)
  const adasProgramId = adasProgramMetaData?.adasProgramId ?? ''

  const baseReportColumns = useMemo<MRT_ColumnDef<BaseReportsTableData>[]>(() => [
    {
      accessorKey: 'reportName',
      id: 'report',
      header: 'Report',
      width: 200,
      enableEditing: true,
    },
    {
      accessorKey: 'dataSetState',
      header: 'Report ' + VAR_COMMON_CONSTANTS.VAR_DATA_SET + ' Status',
      width: 200,
      Cell: ({cell}) => {
        const datasetState = dataSetStatusMap?.[cell.row.original.reportId] ?? ''
        const backgroundColor = ARGOS_REPORTS_STATE_TO_COLOR?.[datasetState] ?? ''
        let label = datasetState
        if (datasetState === EArgosReportsState.PENDING)
          label = `Pending dependent merged line being regenerated`
        else if (datasetState === EArgosReportsState.MODIFIED || datasetState === EArgosReportsState.DELETED)
          label = `${datasetState}: Press Update button`
        if (datasetState === EArgosReportsState.DELETED)
          label += ' to delete report'
        else if (datasetState === EArgosReportsState.MODIFIED)
          label += ' to update report'
        return (
          <span style={{color: backgroundColor, whiteSpace: 'normal'}}>{label}</span>
        )
      },
    },
    {
      accessorKey: 'requirement',
      header: 'Requirement',
      width: 400,
      Cell: ({cell, renderedCellValue}) => {
        const maxStringLength = 200
        const stringLength = String(renderedCellValue).length
        const reportId = cell.row.original.reportId

        return (
          stringLength <= maxStringLength ?
            <div style={{whiteSpace: 'normal'}}>{renderedCellValue}</div>
            :
            <div style={{whiteSpace: 'normal', flexDirection: 'column'}}>
              {String(renderedCellValue).slice(0, maxStringLength)}...
              <span
                style={{color: '#337D98'}}
                onClick={() => dispatch(viewFullRequirementTextDialogSlice[page].openDialog({reportId}))}
                onMouseEnter={(e) => e.currentTarget.style.color = '#1A4D5E'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#337D98'}
              >
                Show more
              </span>
            </div>
        )
      },
    },
    {
      accessorKey: 'reportFormat',
      header: 'Report Format',
      width: 200,
    },
    {
      accessorKey: 'comments',
      header: 'Comments',
      width: 200,
      enableEditing: true,
    },
  ], [dataSetStatusMap, isReadOnlyProgram])

  const redLineReportColumns = useMemo<MRT_ColumnDef<RedLineReportsTableData>[]>(() => [
    {
      accessorKey: 'redLineDataSetName',
      id: 'redLineDataSet',
      header: 'Red Line Data Set',
      width: 280,
      Cell: ({cell}) => {
        const datasetId = cell.row.original.redLineDataSetId
        const datasetName = cell.row.original.redLineDataSetName
        const studyId = cell.row.original.coverageAnalysisStudyId
        const urlForDataset = getUrlForDataset(1, studyId ?? '', datasetId)
        return (
          <ArgosFlexbox>
            {renderLink(datasetName, urlForDataset)}
          </ArgosFlexbox>
        )
      },
    },
    {
      accessorKey: 'redLineDataSetMergeConfiguration',
      header: 'Merge Configuration',
      width: 220,
    },
  ], [dataSetStatusMap, isReadOnlyProgram])

  const greenLineReportColumns = useMemo<MRT_ColumnDef<GreenLineReportsTableData>[]>(() => [
    {
      accessorKey: 'greenLineDataSetName',
      id: 'greenLineDataSet',
      header: 'Green Line Data Set',
    },
    {
      accessorKey: 'sensorName',
      id: 'sensor',
      header: 'Sensor',
    },
    {
      accessorKey: 'sensorLocation',
      header: 'Sensor Location',
    },
    {
      accessorKey: 'sensorModality',
      header: 'Sensor Modality',
    },
  ], [dataSetStatusMap, isReadOnlyProgram])

  const columns = useMemo<MRT_ColumnDef<ReportsTableData>[]>(() => {
    if (page === AdasProgramsReportPages.SENSING_SYSTEM_REQUIREMENT || page === AdasProgramsReportPages.WORLD_MODEL_SOLUTION_REQUIREMENT) {
      return [...baseReportColumns, ...redLineReportColumns] as unknown as MRT_ColumnDef<ReportsTableData>[]
    } else if (page === AdasProgramsReportPages.SENSOR_COMPONENT_REQUIREMENT) {
      return [...baseReportColumns, ...greenLineReportColumns] as unknown as MRT_ColumnDef<ReportsTableData>[]
    } else {
      return []
    }
  }, [page, baseReportColumns, redLineReportColumns, greenLineReportColumns])

  const rows = useAppSelector(tableSlice[page].selectRows) as ReportsTableData[]
  const refRows = React.useRef<ReportsTableData[]>(rows)
  const rowOrder = useAppSelector(tableSlice[page].selectRowOrder)
  const mergeConfigurations = useAppSelector(tableSlice[page].selectMergeConfigurations)

  useEffect(() => {
    refRows.current = rows
  }, [rows, isReadOnlyProgram])

  /**
   * Filter table using merge configuration
   */
  const [selectedMergeConfiguration, setSelectedMergeConfiguration] = useState<string>('All')

  /*****************************************************************************
   * Row Selection
   ****************************************************************************/

  /** Manage row selection state */
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})
  const selectedRows = useAppSelector(tableSlice[page].selectSelectedRows)

  /** Update redux row selection state when table's state changes */
  useEffect(() => {
    dispatch(tableSlice[page].setSelectedRows(rowSelection))
  }, [rowSelection])

  /** Update table's row selection state when redux state changes */
  useEffect(() => {
    setRowSelection(selectedRows)
  }, [selectedRows])


  /** Table Rows */
  const filteredRows = useMemo(() => {
    const _tableData = rows
    if (_tableData != null) {
      let _filteredTableData = _tableData
      if (selectedMergeConfiguration !== 'All') {
        _filteredTableData = _filteredTableData.filter((row) => {
          if (page === AdasProgramsReportPages.SENSING_SYSTEM_REQUIREMENT || page === AdasProgramsReportPages.WORLD_MODEL_SOLUTION_REQUIREMENT) {
            return 'redLineDataSetMergeConfiguration' in row && row.redLineDataSetMergeConfiguration === selectedMergeConfiguration
          } else if (page === AdasProgramsReportPages.SENSOR_COMPONENT_REQUIREMENT) {
            return 'sensorNameLocation' in row && row.sensorNameLocation === selectedMergeConfiguration
          }
        })
      }

      if (rowOrder?.length > 0) {
        const clonedTable = cloneDeep(_filteredTableData)
        _filteredTableData = orderTableDataByReportOrder(clonedTable, rowOrder)
      }
      return _filteredTableData
    } else {
      return rows
    }
  }, [rows, selectedMergeConfiguration, rowOrder])


  /** Row Reordering */
  const [isReportsReordering, setIsRowReorderLoading] = useState<boolean>(false)

  const setRowOrderHandle = usePostRequest(tableSlice[page].setRowOrderEndpoint)

  /** Table Actions */
  const [reportId, setReportId] = useQueryParam('reportId', StringParam)

  const onClickAction = (action: ReportsTableRowActionTypes, _reportId: string) => {
    switch (action) {
      case ReportsTableRowActionTypes.VIEW_REPORT:
        setReportId(_reportId)
        dispatch(viewReportDialogSlice[page].setIsDialogOpen(true))
        break
      case ReportsTableRowActionTypes.EDIT_REPORT:
        setReportId(_reportId)
        dispatch(editReportDialogSlice[page].setIsDialogOpen(true))
        break
    }
  }

  /**
   * Fetch data into coverage requirements table tab
   */
  const getReportsTableUrl = tableSlice[page].getReportsTableUrl
  const getReportsTableHandle = usePostRequest(getReportsTableUrl)
  const [isGetReportsTableLoading, setIsGetReportsTableLoading] = useState(true)
  const getReportsTable = (onlyIfOutdated: boolean = false) => {
    setIsGetReportsTableLoading(true)
    getReportsTableHandle({
      adasProgramId,
      onlyIfOutdated,
    }).then((res: ReportsTableResponse) => {
      if (onlyIfOutdated) {
        return
      }
      filterAndSetTableData(res, dispatch, page)
    }).catch((err) => {
      console.log('Get reports table failed', err)
      toast.error('Get reports table failed')
    }).finally(() => {
      setIsGetReportsTableLoading(false)
    })
  }

  useEffect(() => {
    if (adasProgramId && adasProgramId !== '') {
      getReportsTable()
    }
  }, [adasProgramId])

  const isSavingTable = useAppSelector((state) => tableSlice[page].selectTypeLoadingState(state, EAdasProgramsLoadingStateTypes.EDIT_REPORT))
  const [isEditingTable, setIsEditingTable] = useState(false)
  const isSaved = useAppSelector(tableSlice[page].selectIsSaved)

  const isTablePendingSave = useMemo(() => {
    return !isEditingTable && !isSaved && !isSavingTable
  }, [isEditingTable, isSaved, isSavingTable])

  const handleSaveTable = usePostRequest(tableSlice[page].saveTableEndpoint)
  const saveTable = () => {
    dispatch(tableSlice[page].setLoadingState({[EAdasProgramsLoadingStateTypes.EDIT_REPORT]: {isLoading: true}}))
    handleSaveTable({
      adasProgramId,
      tableData: rows,
    }).then((res: ReportsTableResponse) => {
      filterAndSetTableData(res, dispatch, page)
    }).catch((err) => {
      console.log('Save table failed', err)
      toast.error('Save table failed')
    }).finally(() => {
      dispatch(tableSlice[page].setLoadingState({[EAdasProgramsLoadingStateTypes.EDIT_REPORT]: {isLoading: false}}))
    })
  }

  const getIndexGivenReportId = (_tableData: ReportsTableData[], reportId: string) => {
    return _tableData.findIndex((row) => row.reportId === reportId)
  }

  const handleSaveCell = (cell: MRT_Cell<ReportsTableData>, value: string) => {
    const _tableData = cloneDeep(rows)
    const realTableIndex = getIndexGivenReportId(_tableData, cell.row.original.reportId)  // This cell prop has thte filtered index, but we need to find the index of this report in raw table data.

    _tableData[realTableIndex][cell.column.id as keyof ReportsTableData] = value as EArgosReportsState
    dispatch(tableSlice[page].setRows(_tableData))
    dispatch(tableSlice[page].setIsSaved(false))
  }

  /** Check if any outdated reports */
  const [anyOutdatedReports, setAnyOutdatedReports] = useState(false)

  const checkIfOutdatedDatasetStatesInTable = (_dataSetStatusMap: { [reportId: string]: string }) => {
    if (!_dataSetStatusMap) return false
    const datasetStates = new Set(Object.values(_dataSetStatusMap))
    return datasetStates.has(EArgosReportsState.MODIFIED) || datasetStates.has(EArgosReportsState.DELETED)
  }

  useEffect(() => {
    const hasOutdated = checkIfOutdatedDatasetStatesInTable(dataSetStatusMap)
    setAnyOutdatedReports(hasOutdated)
  }, [dataSetStatusMap])

  const isSaveDisabled = isSavingTable || !isTablePendingSave
  const isDeleteDisabled = Object.keys(useAppSelector(deleteReportDialogSlice[page].selectSelectedRows)).length === 0

  const leftToolbarProps = useMemo(() => {
    return {
      page,
      setSelectedMergeConfiguration,
      selectedMergeConfiguration,
      mergeConfigurations,
    }
  }, [page, selectedMergeConfiguration, mergeConfigurations])

  const rightToolbarProps = useMemo(() => {
    return {
      page,
      getReportsTable,
      saveTable,
      isSaveDisabled,
      isDeleteDisabled,
      anyOutdatedReports,
      isGetReportsTableLoading,
    }
  }, [page, isSaveDisabled, isDeleteDisabled, anyOutdatedReports, isGetReportsTableLoading])

  const rowActionsProps = useMemo(() => {
    return {
      adasProgramId,
      page,
      dispatch,
      onClickAction,
      isReadOnlyProgram,
    }
  }, [page, adasProgramId, isReadOnlyProgram])


  return (
    <ArgosFlexbox vertical style={{height: '100%', width: '100%'}}>
      <ArgosTableSimple
        columns={columns}
        data={filteredRows}

        /** State management */
        state={{
          isLoading: isGetReportsTableLoading,
          showSkeletons: isGetReportsTableLoading || isReportsReordering,
          rowSelection: rowSelection
        }}
        initialState={{
          columnVisibility: {
            rationale: false,
            upstreamDependencies: false,
            vehicleProgramTestCases: false
          },
          density: 'compact',
        }}

        /** Row selection */
        enableRowSelection={true}
        enableBatchRowSelection={true}
        getRowId={(row) => row.reportId}
        onRowSelectionChange={setRowSelection}

        /** Row ordering */
        enableRowOrdering={filteredRows?.length > 1}
        muiRowDragHandleProps={({table}) => ({
          onDragEnd: () => {
            const {draggingRow, hoveredRow} = table.getState()
            if (hoveredRow && draggingRow) {
              setIsRowReorderLoading(true)
              const newRowOrder = cloneDeep(rowOrder)

              /** Get affected row ID indices */
              const draggingRowId = draggingRow.original.reportId
              const hoveredRowId = hoveredRow?.original?.reportId
              const draggingRowIdIndex = newRowOrder.indexOf(draggingRowId)
              const hoveredRowIdIndex = newRowOrder.indexOf(hoveredRowId)

              /** Set new row order */
              const [removedReport] = newRowOrder.splice(draggingRowIdIndex, 1)
              newRowOrder.splice(hoveredRowIdIndex, 0, removedReport)

              /** Send request with new order */
              setRowOrderHandle({
                adasProgramId,
                newReportOrder: newRowOrder,
              }).then(() => {
                /** Response is sent through socket */
              }).catch((err) => {
                console.log('An error occurred while reordering the table rows', err)
                toast.error('An error occurred while reordering the table rows')
              }).finally(() => {
                setIsRowReorderLoading(false)
              })
            }
          },
        })}

        /** Cell editing */
        enableEditing={!isReadOnlyProgram}
        editDisplayMode={'cell'}
        muiEditTextFieldProps={({cell}) => ({
          onBlur: (event) => {
            setIsEditingTable(false)
            handleSaveCell(cell, event.target.value);
          },
          onFocus: () => {
            setIsEditingTable(true)
          }
        })}

        /** Toolbar */
        renderToolbarInternalActions={({table}) => RightToolbarActions(table, rightToolbarProps)}
        renderTopToolbarCustomActions={({table}) => LeftToolbarActions(table, leftToolbarProps)}

        /** Row actions */
        enableRowActions={hasWriteAccess && !isReadOnlyProgram}
        positionActionsColumn="last"
        displayColumnDefOptions={{
          'mrt-row-actions': {
            size: hasWriteAccess ? 230 : 120,
          }
        }}
        renderRowActions={({row}) => {
          return RowActions(row, rowActionsProps)
        }}

        /** Layout */
        muiTableProps={{
          sx: {
            tableLayout: 'fixed',
          },
        }}
      />
      <ViewFullRequirementTextDialog/>
      <ManageDataSourcesDialog/>
      <NewReportDialog/>
      <ViewReportDialog/>
      <EditReportDialog/>
      <UpdateReportDialog/>
      <DeleteReportDialog/>
    </ArgosFlexbox>
  )
}
